import { i18n } from '@quadralize/i18n'
import * as z from 'zod'
import {
  validateCep,
  validateCnpj,
  validateCpf,
  validateCpfCnpj,
  validatePhone,
} from '../masks/masks'

interface RequiredFieldOptions {
  required: true
  requiredMessage?: string
  invalidMessage?: string
}

interface OptionalFieldOptions {
  required?: false
  invalidMessage?: string
}

export type ZodFieldOptions = RequiredFieldOptions | OptionalFieldOptions

type Validator = (value: string) => boolean

type FormsValidationKey =
  | 'phoneInvalid'
  | 'cepInvalid'
  | 'cpfInvalid'
  | 'cnpjInvalid'
  | 'documentInvalid'
  | 'emailInvalid'

/**
 * Resolves a translation key from the shared `forms` namespace at validation time.
 * Lazy by design — the call defers until Zod actually evaluates the error message,
 * which is always after `initI18n()` has run during app bootstrap.
 */
const tForms = (key: 'required' | `validation.${FormsValidationKey}`): string =>
  i18n.t(`forms:${key}` as const) as string

function buildField(validate: Validator, defaultInvalidKey: FormsValidationKey) {
  function field(options: RequiredFieldOptions): z.ZodType<string, string>
  function field(options?: OptionalFieldOptions): z.ZodType<string | null, string | null>
  function field(
    options: ZodFieldOptions = {},
  ): z.ZodType<string | null, string | null> | z.ZodType<string, string> {
    const getInvalidMessage = () =>
      options.invalidMessage ?? tForms(`validation.${defaultInvalidKey}`)

    if (options.required) {
      const getRequiredMessage = () => options.requiredMessage ?? tForms('required')
      return z
        .string()
        .min(1, { error: getRequiredMessage })
        .refine(validate, { error: getInvalidMessage }) as z.ZodType<string, string>
    }

    return z
      .string()
      .nullable()
      .refine((value) => !value || validate(value), {
        error: getInvalidMessage,
      }) as z.ZodType<string | null, string | null>
  }

  return field
}

/**
 * Reusable Zod schema for Brazilian phone numbers (10 or 11 digits, post-mask).
 *
 * - `zPhone()` — optional, accepts `null` or empty string and validates only if filled.
 * - `zPhone({ required: true })` — must be filled and valid.
 *
 * Default messages are pulled from the shared `forms` i18n namespace
 * (`forms.required`, `forms.validation.phoneInvalid`). Pass `requiredMessage`
 * or `invalidMessage` to override per call site.
 *
 * Pair with `<FormInput mask="phone" />` so the stored value is already clean digits.
 */
export const zPhone = buildField(validatePhone, 'phoneInvalid')

/**
 * Reusable Zod schema for Brazilian CEP (8 digits, post-mask).
 * Default invalid message: `forms.validation.cepInvalid`.
 */
export const zCep = buildField(validateCep, 'cepInvalid')

/**
 * Reusable Zod schema for CPF, validated via the Receita Federal check digit algorithm.
 * Default invalid message: `forms.validation.cpfInvalid`.
 */
export const zCpf = buildField(validateCpf, 'cpfInvalid')

/**
 * Reusable Zod schema for CNPJ (alphanumeric — Receita Federal 2026 spec).
 * Default invalid message: `forms.validation.cnpjInvalid`.
 */
export const zCnpj = buildField(validateCnpj, 'cnpjInvalid')

/**
 * Reusable Zod schema for unified CPF/CNPJ — auto-detects the type by length.
 * Default invalid message: `forms.validation.documentInvalid`.
 *
 * Pair with `<FormInput mask="cpfCnpj" />`.
 */
export const zCpfCnpj = buildField(validateCpfCnpj, 'documentInvalid')

/**
 * Reusable Zod schema for emails.
 *
 * - `zEmail()` — optional. Accepts a valid email, empty string, or `null`. Empty strings
 *   are normalized to `null` on parse. Output type: `string | null`.
 * - `zEmail({ required: true })` — must be filled and valid. Output type: `string`.
 *
 * Default messages: `forms.required` + `forms.validation.emailInvalid`.
 */
export function zEmail(options: RequiredFieldOptions): z.ZodType<string, string>
export function zEmail(options?: OptionalFieldOptions): z.ZodType<string | null, string | null>
export function zEmail(
  options: ZodFieldOptions = {},
): z.ZodType<string | null, string | null> | z.ZodType<string, string> {
  const getInvalidMessage = () => options.invalidMessage ?? tForms('validation.emailInvalid')

  if (options.required) {
    const getRequiredMessage = () => options.requiredMessage ?? tForms('required')
    return z.preprocess(
      (value) => {
        if (value == null) return ''
        return typeof value === 'string' ? value.trim() : value
      },
      z
        .string()
        .min(1, { error: getRequiredMessage })
        .pipe(z.email({ error: getInvalidMessage })),
    ) as z.ZodType<string, string>
  }

  return z
    .union([z.email({ error: getInvalidMessage }), z.literal(''), z.null()])
    .transform((value) => (value === '' ? null : value)) as z.ZodType<string | null, string | null>
}
